<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/subjects/${subjectId}/topics`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit Topic</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-8 mb-3">
                  <label class="form-label">Topic Name <span class="text-danger">*</span></label>
                  <input v-model="form.name" type="text" class="form-control" required />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Display Order</label>
                  <input v-model.number="form.order" type="number" class="form-control" min="1" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="3"></textarea>
              </div>

              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 flex-wrap pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update Topic
                </button>
                <router-link :to="`/app/topics/${topicId}/lessons`" class="btn btn-outline-info">
                  <vue-feather type="book-open" size="14" class="me-1" />
                  Manage Lessons
                </router-link>
                <router-link :to="`/app/topics/${topicId}/notes`" class="btn btn-outline-warning">
                  <vue-feather type="file-text" size="14" class="me-1" />
                  Manage Notes
                </router-link>
                <router-link :to="`/app/subjects/${subjectId}/topics`" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditTopic',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const topicId = route.params.id

    const topic = ref(null)
    const subjectId = ref(null)
    const form = ref({
      name: '',
      description: '',
      order: 1,
      status: 'draft'
    })
    const loading = ref(false)
    const error = ref('')

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      // Exam → Subject → Topic (edit)
      if (topic.value?.examId) {
        items.push({ title: topic.value?.examName || 'Exam', url: `/app/exams/${topic.value.examId}/subjects` })
      }
      if (topic.value?.subjectId) {
        items.push({ title: topic.value?.subjectName || 'Subject', url: `/app/subjects/${topic.value.subjectId}/topics` })
      }
      items.push({ title: topic.value?.name || 'Edit Topic', active: true })
      return items
    })

    const fetchTopic = async () => {
      try {
        const response = await api.get(`/v1/admin/topics/${topicId}`)
        topic.value = response.data.topic
        subjectId.value = response.data.topic.subjectId
        form.value = {
          name: response.data.topic.name,
          description: response.data.topic.description || '',
          order: response.data.topic.order || 1,
          status: response.data.topic.status || 'draft'
        }
      } catch (err) {
        alert.error('Error', 'Topic not found')
        router.push('/app/exams/list')
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`/v1/admin/topics/${topicId}`, form.value)
        alert.success('Success', 'Topic updated successfully')
        router.push(`/app/subjects/${subjectId.value}/topics`)
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update topic'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchTopic)

    return { topicId, subjectId, form, loading, error, breadcrumbs, handleSubmit }
  }
}
</script>
